/**
 * The app store. Single source of truth. Subscribed via useSyncExternalStore
 * so React gets fine-grained re-renders and tearing is impossible.
 *
 * We use structural edits (immutable setAtPointer) so undo/redo can be added
 * later as just push/pop of snapshots without changing any reducer code.
 */

import {
  deleteAtPointer,
  getAtPointer,
  moveArrayItemAtPointer,
  moveObjectKeyAtPointer,
  parentPointer as parentOfPointer,
  renameKeyAtPointer,
  setAtPointer,
  validate,
  type JsonPointer,
  type ValidationIssue,
} from '@bliksys/schema-engine';
import type { JsonSchema, JsonValue } from '@bliksys/shared';

export interface AppState {
  schema: JsonSchema;
  /**
   * The working copy — what the editor renders and edits. While a
   * draft is active, this contains the user's in-progress edits. When
   * no draft is active, `data === committed`.
   */
  data: JsonValue;
  /**
   * The applied snapshot — the canonical state that downloads, deploys,
   * and saves consume. Diverges from `data` only when a draft is
   * active. Apply: `committed = data`. Discard: `data = committed`.
   *
   * Why a separate slot: without it, an in-progress draft (a new
   * array item that's still being filled in) leaks into the JSON the
   * user downloads or deploys, even when the draft is invalid. The
   * working copy is for the user's eyes; the committed copy is what
   * the world sees.
   */
  committed: JsonValue;
  /** Current zoom-in pointer. Root is ''. */
  pointer: JsonPointer;
  /** Dirty flag — committed differs from the last-saved snapshot. */
  dirty: boolean;
  /** Validation issues against the working copy (so the editor surfaces them live). */
  issues: readonly ValidationIssue[];
  /**
   * When set, marks a sub-tree the user explicitly entered to author or
   * edit. Apply commits the working copy to committed; Discard rolls
   * back the working copy to committed. The Editor surfaces Apply/
   * Discard buttons in the header when the current pointer is at or
   * below this draft pointer; navigating outside the draft sub-tree
   * prompts the user to resolve.
   */
  draftPointer: JsonPointer | null;
  /**
   * Whether the active draft is a freshly-added item ('create') or an
   * existing item the user is editing in place ('edit'). Affects the
   * wording of the Discard confirmation and conceptually nothing else
   * — both kinds discard via "data = committed" rollback.
   */
  draftKind: 'create' | 'edit' | null;
  /**
   * True when the active draft has actual user edits since beginDraft
   * was called. Used by navigation guards to suppress the "Unapplied
   * changes" prompt when the user merely zoomed into a row to look
   * at it and clicked Back without touching anything.
   *
   * Reset to false on beginDraft (edit), true on beginCreate (because
   * the create itself is a change), set to true on any commitData
   * while a draft is active, cleared on Apply / Discard / replace.
   *
   * Note: this is a one-way flag — once true, stays true until Apply/
   * Discard. Reverting an edit back to its original value still leaves
   * the flag set, which is conservative: the user touched the field
   * and might consider that a "change" worth being prompted about.
   */
  draftHasChanges: boolean;
  /**
   * Registry of fetched remote-ref schemas, keyed by URL. The schema
   * engine consults this to resolve `$ref: 'https://example.com/...'`.
   * Populated by the fetch-on-load pipeline (see `remote-refs.ts`).
   * Empty when no remote refs are present in the schema.
   */
  refRegistry: ReadonlyMap<string, JsonSchema>;
  /**
   * Per-URL error messages for remote refs that failed to fetch. The
   * editor surfaces these as a banner so the user knows why parts of
   * the schema are showing "schema not loaded" rather than rendering
   * normally. Empty when all fetches succeeded.
   */
  refFetchErrors: ReadonlyMap<string, string>;
}

type Listener = () => void;

export class Store {
  private state: AppState;
  private listeners = new Set<Listener>();
  private lastSaved: JsonValue;

  constructor(initial: { schema: JsonSchema; data: JsonValue }) {
    this.lastSaved = initial.data;
    this.state = {
      schema: initial.schema,
      data: initial.data,
      committed: initial.data,
      pointer: '',
      dirty: false,
      issues: validate(initial.data, initial.schema, initial.schema),
      draftPointer: null,
      draftKind: null,
      draftHasChanges: false,
      // Remote refs are fetched asynchronously after construction. The
      // host app calls setRefRegistry(...) once the fetches resolve.
      // Until then, remote $refs in the schema produce "schema not
      // loaded" errors at render time — graceful degradation rather
      // than a blank screen.
      refRegistry: new Map(),
      refFetchErrors: new Map(),
    };
  }

  /**
   * Set the registry of fetched remote-ref schemas (and any per-URL
   * fetch errors). Called by the host app's fetch pipeline once
   * `fetchAllRemoteRefs(schema)` resolves. Triggers a re-render so
   * any "schema not loaded" placeholders flip to real editors.
   *
   * Validates against the new registry — issues are recomputed because
   * remote $refs that previously couldn't resolve might now reveal
   * required fields or other constraints.
   */
  setRefRegistry(
    registry: ReadonlyMap<string, JsonSchema>,
    errors: ReadonlyMap<string, string>,
  ): void {
    const issues = validate(
      this.state.data,
      this.state.schema,
      this.state.schema,
      undefined,
      registry,
    );
    this.set({ refRegistry: registry, refFetchErrors: errors, issues });
  }

  getState = (): AppState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const l of this.listeners) l();
  }

  /** Replace the state and notify. Kept private so all mutations go through actions. */
  private set(next: Partial<AppState>): void {
    this.state = { ...this.state, ...next };
    this.emit();
  }

  /* ---------------- actions ---------------- */

  navigate(pointer: JsonPointer): void {
    if (pointer === this.state.pointer) return;
    this.set({ pointer });
  }

  /**
   * True when navigating from `from` to `to` would cross the boundary
   * of the active draft (i.e. the user would end up outside the draft
   * sub-tree). Used by the editor to prompt for Apply/Discard before
   * letting the user leave a draft via breadcrumb, back button, close,
   * or any other navigation path.
   *
   * Returns false when there's no active draft, or when the
   * destination is still inside the draft sub-tree.
   */
  navigationLeavesDraft(to: JsonPointer): boolean {
    const draft = this.state.draftPointer;
    if (draft === null) return false;
    if (to === draft) return false;
    if (to.startsWith(`${draft}/`)) return false;
    return true;
  }

  /**
   * Mark a sub-tree as a draft. The current `data` becomes the working
   * copy; `committed` is captured as the rollback target. The editor
   * surfaces Apply/Discard buttons in the header when the current
   * pointer is at or below this draft pointer.
   *
   * The caller is responsible for any data mutations (e.g. inserting a
   * new array item with `setValue` BEFORE calling this) — beginDraft
   * itself does not mutate `data`. It just snapshots whatever's there
   * as the rollback target.
   *
   * For `kind: 'create'`: the typical sequence is
   *   1. setValue(newPointer, defaultValue)  // adds the item
   *   2. beginDraft(newPointer, 'create')    // snapshots OUT-OF-DATE
   * The snapshot must be taken BEFORE the setValue. Use the helper
   * `beginCreate(parentPointer, addFn, kind)` to do this atomically.
   *
   * For `kind: 'edit'`: just call beginDraft(itemPointer, 'edit').
   * The current data becomes both committed and working; subsequent
   * setValue calls diverge them.
   */
  beginDraft(pointer: JsonPointer, kind: 'create' | 'edit' = 'edit'): void {
    this.set({
      committed: this.state.data,
      draftPointer: pointer,
      draftKind: kind,
      // Edit drafts start unchanged — the user just zoomed in. Each
      // commitData below will flip the flag if they actually edit
      // something. Create drafts get true through beginCreate.
      draftHasChanges: false,
    });
  }

  /**
   * Atomic create-with-draft: snapshot first, then apply the mutation,
   * then mark the draft. Use this instead of `setValue` + `beginDraft`
   * when adding a new item, otherwise the snapshot captures the
   * already-added item and Discard becomes a no-op.
   *
   * `mutate` is called with the current data and should return the
   * data with the new item inserted. Returns the pointer to the new
   * item so the caller can navigate into it.
   */
  beginCreate(mutate: (data: JsonValue) => { next: JsonValue; pointer: JsonPointer }): JsonPointer {
    const before = this.state.data;
    const { next, pointer: newPointer } = mutate(before);
    if (next === before) {
      // No change — bail. Should not normally happen.
      return newPointer;
    }
    const issues = validate(
      next,
      this.state.schema,
      this.state.schema,
      undefined,
      this.state.refRegistry,
    );
    this.set({
      data: next,
      committed: before,
      issues,
      // dirty tracks committed vs lastSaved — committed didn't move,
      // so a fresh draft alone doesn't dirty the document. Only the
      // eventual Apply does.
      dirty: before !== this.lastSaved,
      draftPointer: newPointer,
      draftKind: 'create',
      // The act of creating IS a change — the new item is sitting in
      // working but not in committed. Discard would remove it; the
      // user needs the prompt.
      draftHasChanges: true,
      pointer: newPointer,
    });
    return newPointer;
  }

  /**
   * Confirm the draft: working copy becomes committed. The caller is
   * responsible for navigation after Apply (typically zoom out to the
   * draft's parent).
   *
   * Refuses (returns false) when the draft sub-tree has validation
   * issues — the canonical state must be consistent. The caller should
   * surface that as a toast or inline error rather than silently
   * applying invalid data.
   */
  applyDraft(): boolean {
    const draft = this.state.draftPointer;
    if (draft === null) return false;
    if (this.draftHasIssues()) return false;
    const parts = draft.split('/');
    parts.pop();
    const parent = parts.join('/');
    const newCommitted = this.state.data;
    this.set({
      committed: newCommitted,
      dirty: newCommitted !== this.lastSaved,
      draftPointer: null,
      draftKind: null,
      draftHasChanges: false,
      pointer: parent,
    });
    return true;
  }

  /**
   * Throw away the draft: working copy rolls back to committed. For
   * 'create' drafts this effectively removes the new item (since
   * committed didn't have it). For 'edit' drafts this restores the
   * pre-edit state of the sub-tree.
   *
   * The user always lands at the parent pointer after Discard, even
   * for edits — leaving them inside a sub-tree they just discarded
   * would be confusing.
   */
  discardDraft(): void {
    const draft = this.state.draftPointer;
    if (draft === null) return;
    const parts = draft.split('/');
    parts.pop();
    const parent = parts.join('/');
    const committed = this.state.committed;
    const issues = validate(
      committed,
      this.state.schema,
      this.state.schema,
      undefined,
      this.state.refRegistry,
    );
    this.set({
      data: committed,
      issues,
      dirty: committed !== this.lastSaved,
      draftPointer: null,
      draftKind: null,
      draftHasChanges: false,
      pointer: parent,
    });
  }

  /**
   * True when validation issues exist anywhere within the active
   * draft's sub-tree. Used to gate Apply.
   *
   * Note the path-format mismatch: validation issues use human-
   * readable dotted paths (`pages[0].title`), while pointers are JSON
   * Pointer (`/pages/0/title`). We convert here so the prefix match
   * works.
   */
  draftHasIssues(): boolean {
    const draft = this.state.draftPointer;
    if (draft === null) return false;
    const dotted = pointerToDotted(draft);
    return this.state.issues.some(
      (issue) =>
        issue.path === dotted ||
        issue.path.startsWith(`${dotted}.`) ||
        issue.path.startsWith(`${dotted}[`),
    );
  }

  /**
   * Clear the draft state without applying or rolling back. Equivalent
   * to discardDraft for cases where `draftHasChanges` is false (the
   * working copy already matches committed) — used by navigation
   * guards to silently resolve a draft when the user zoomed into a
   * row but didn't make any edits.
   *
   * Unlike discardDraft, this does NOT navigate. The caller is
   * responsible for setting the new pointer.
   *
   * Safe ONLY when there are no changes — calling this with edits in
   * flight would silently leak them into committed-equivalent state
   * via the next setValue, which is exactly the bug we're trying to
   * avoid. Guarded with an assertion in dev to catch misuse.
   */
  clearDraft(): void {
    if (this.state.draftPointer === null) return;
    if (this.state.draftHasChanges) {
      // Defensive: misuse. Falling back to discard is safer than
      // silently dropping divergent edits — at least the user lands
      // on committed state, not a half-applied limbo.
      this.discardDraft();
      return;
    }
    this.set({
      draftPointer: null,
      draftKind: null,
      draftHasChanges: false,
    });
  }

  /**
   * True when the active draft has actual user edits since beginDraft
   * was called. Returns false when there's no draft, OR when the user
   * zoomed into a row but hasn't touched anything yet.
   *
   * Used by navigation guards to suppress the "Unapplied changes"
   * prompt when the user merely zoomed into a row to look at it and
   * clicks back without touching anything. In that case Apply is a
   * no-op (committed = data already) so silent resolution is correct.
   *
   * Implementation: tracked as a one-way state flag rather than a
   * deep-equal comparison. Reset on beginDraft (edit), set true on
   * beginCreate (the create itself is a change), set true on any
   * commitData while a draft is active, cleared on Apply / Discard.
   * Once true within a draft, stays true — reverting an edit back to
   * its original value still leaves the flag set, which is conservative:
   * the user touched the field and might consider that a change worth
   * being prompted about.
   */
  draftHasChanges(): boolean {
    return this.state.draftHasChanges;
  }

  /**
   * The canonical state for downloads, deploys, and saves. While a
   * draft is active this is the pre-draft snapshot; otherwise it's
   * just `data`. Callers should prefer this over `state.data` for
   * any export-side flow.
   */
  getCommitted(): JsonValue {
    return this.state.committed;
  }

  /**
   * The current registry of fetched remote-ref schemas. Callers
   * (typically the editor's `BuildContext`) use this so the engine
   * can resolve `$ref: 'https://...'` URLs.
   */
  getRefRegistry(): ReadonlyMap<string, JsonSchema> {
    return this.state.refRegistry;
  }

  setValue(pointer: JsonPointer, value: JsonValue): void {
    const nextData = setAtPointer(this.state.data, pointer, value);
    this.commitData(nextData);
  }

  deleteValue(pointer: JsonPointer): void {
    const nextData = deleteAtPointer(this.state.data, pointer);
    this.commitData(nextData);
  }

  /**
   * Rename a key within an object: read the value at `oldPointer`, write
   * it to `newPointer`, and delete the old. Atomic — the data only
   * snapshots once. Used by the schema editor when the developer renames
   * a property (e.g. "tilte" → "title") without losing the configured
   * constraints.
   */
  renameKey(oldPointer: JsonPointer, newPointer: JsonPointer): void {
    const next = renameKeyAtPointer(this.state.data, oldPointer, newPointer);
    this.commitData(next);
  }

  /**
   * Reorder a key within an object map. The object at `parentPointer`
   * is rebuilt with `key` moved to the position before `beforeKey`, or
   * to the end if `beforeKey` is null. Other keys preserve their
   * relative order. Used by the schema editor to drag-reorder
   * `properties` map entries.
   */
  moveObjectKey(parentPointer: JsonPointer, key: string, beforeKey: string | null): void {
    const next = moveObjectKeyAtPointer(this.state.data, parentPointer, key, beforeKey);
    this.commitData(next);
  }

  /**
   * Reorder a key within an object map AND record the new full key
   * order in the grandparent schema's `x-bliksys.propOrder`. Used by
   * the schema editor when the user reorders the `properties` map of
   * an object schema.
   *
   * Why both: insertion order in the `properties` map is the natural
   * place to record field order, but JSON Schema doesn't formally
   * specify property iteration order — some serializers sort
   * alphabetically across round-trips. Writing `x-bliksys.propOrder`
   * makes the order survive any JSON encoder. The engine reads
   * propOrder when present (see build.ts).
   *
   * Pointers: `parentPointer` should be the properties map itself
   * (e.g. `/properties` or `/properties/foo/properties`). The
   * grandparent — one level up — is the schema whose properties are
   * being reordered. We write `x-bliksys.propOrder` on that
   * grandparent. If parentPointer doesn't end in `/properties`, fall
   * back to a plain key reorder so the method behaves like
   * `moveObjectKey` for unsupported shapes.
   */
  reorderSchemaProperty(parentPointer: JsonPointer, key: string, beforeKey: string | null): void {
    const moved = moveObjectKeyAtPointer(this.state.data, parentPointer, key, beforeKey);
    if (moved === this.state.data) return;
    // Only write propOrder when the move target IS a JSON Schema's
    // properties map. Other paths (content data with a key named
    // "properties" by coincidence) get plain reorder semantics.
    if (!parentPointer.endsWith('/properties') && parentPointer !== '/properties') {
      this.commitData(moved);
      return;
    }
    const grandPointer = parentOfPointer(parentPointer);
    if (grandPointer === null) {
      this.commitData(moved);
      return;
    }
    // Read the new full order from the moved data, and write that as
    // propOrder on the grandparent. Preserves the rest of the
    // grandparent schema unchanged.
    const reorderedMap = getAtPointer(moved, parentPointer);
    if (
      reorderedMap === null ||
      reorderedMap === undefined ||
      typeof reorderedMap !== 'object' ||
      Array.isArray(reorderedMap)
    ) {
      this.commitData(moved);
      return;
    }
    const newOrder = Object.keys(reorderedMap);
    const grandparent = getAtPointer(moved, grandPointer);
    if (
      grandparent === null ||
      grandparent === undefined ||
      typeof grandparent !== 'object' ||
      Array.isArray(grandparent)
    ) {
      this.commitData(moved);
      return;
    }
    const existingHints = grandparent['x-bliksys'];
    const nextHints =
      existingHints !== null && typeof existingHints === 'object' && !Array.isArray(existingHints)
        ? { ...existingHints, propOrder: newOrder }
        : { propOrder: newOrder };
    const nextGrand = { ...grandparent, 'x-bliksys': nextHints };
    const withHints = setAtPointer(moved, grandPointer, nextGrand);
    this.commitData(withHints);
  }

  /**
   * Reorder an item within an array. `arrayPointer` points at the array
   * itself; `from` and `to` are indices. Out-of-range or no-op moves
   * are silently ignored.
   */
  moveArrayItem(arrayPointer: JsonPointer, from: number, to: number): void {
    const next = moveArrayItemAtPointer(this.state.data, arrayPointer, from, to);
    this.commitData(next);
  }

  /**
   * Internal: shared tail of any data-mutating method. No-ops when the
   * value didn't change (engine ops return the same reference). Otherwise
   * revalidates and updates dirty flag.
   *
   * When a draft is active, mutations land in the working copy only —
   * `committed` is left untouched until Apply. When no draft is active,
   * `data` and `committed` are kept in sync (the user's edit IS the
   * canonical state immediately).
   *
   * The `dirty` flag tracks whether the canonical state (`committed`)
   * differs from the last-saved snapshot — not whether the working
   * copy diverged. Saving an in-progress draft would persist the same
   * thing as before the draft started.
   */
  private commitData(next: JsonValue): void {
    if (next === this.state.data) return;
    const issues = validate(
      next,
      this.state.schema,
      this.state.schema,
      undefined,
      this.state.refRegistry,
    );
    if (this.state.draftPointer === null) {
      // No draft active — working IS canonical.
      this.set({ data: next, committed: next, dirty: next !== this.lastSaved, issues });
    } else {
      // Draft active — mutate working only. Dirty flag reflects committed,
      // which is unchanged in this branch. Mark the draft as having
      // changes so navigation guards know to prompt before leaving.
      this.set({ data: next, issues, draftHasChanges: true });
    }
  }

  /**
   * Mark the canonical (committed) state as saved. Wire this to your
   * real save flow (download JSON, PR, webhook, DB, ...).
   */
  markSaved(): void {
    this.lastSaved = this.state.committed;
    this.set({ dirty: false });
  }

  replaceDocument(next: { schema?: JsonSchema; data?: JsonValue }): void {
    const schema = next.schema ?? this.state.schema;
    const data = next.data ?? this.state.data;
    this.lastSaved = data;
    this.set({
      schema,
      data,
      committed: data,
      pointer: '',
      dirty: false,
      issues: validate(data, schema, schema),
      draftPointer: null,
      draftKind: null,
      draftHasChanges: false,
      // Different schema → fresh fetch. The host app refires the remote-
      // ref pipeline against the new schema and calls setRefRegistry.
      refRegistry: new Map(),
      refFetchErrors: new Map(),
    });
  }
}

/**
 * Convert a JSON Pointer to the dotted/bracketed path format the
 * validator emits. /pages/0/title → pages[0].title. Empty pointer is
 * the empty string.
 *
 * Lives at module scope so the Store class can use it without leaking
 * a private dependency on the editor's display formatter.
 */
function pointerToDotted(pointer: string): string {
  if (pointer === '') return '';
  return pointer
    .slice(1)
    .split('/')
    .map((seg) => seg.replace(/~1/g, '/').replace(/~0/g, '~'))
    .reduce((acc, seg) => {
      if (/^\d+$/.test(seg)) return `${acc}[${seg}]`;
      return acc ? `${acc}.${seg}` : seg;
    }, '');
}
